<!-- Shelf — the wall. Tab root, full-bleed: a photographic café wall of pipe
     shelving and oak (ShelfWall) under a floating glass title card, with the
     dock at its foot (Group by, Filter, AR, the bay strip) sitting on the tab
     bar. The card IS the header: AppHeader's toolbar, restyled as glass, with
     About leading and the list toggle trailing.

     Two views of one thing: the wall and the list. They share Group by,
     Filter and the picked coffee; the list is the parity path for screen
     readers (the skip link is first in focus order). -->
<template>
  <IonPage class="shelf-page" :class="`shelf-page--${view}`">
    <button v-if="view === 'wall'" type="button" class="skip" @click="setView('list')">Switch to list view</button>

    <AppHeader title="Shelf" class="shelf-header">
      <IonButtons slot="start">
        <IonButton aria-label="About BeanHub Shelf" @click="goAbout">
          <IonIcon slot="icon-only" :icon="informationCircleOutline" />
        </IonButton>
      </IonButtons>
      <div v-if="view === 'wall'" class="card" aria-live="polite">
        <div class="card__text">
          <h1 class="card__title">{{ cardTitle }}</h1>
          <p class="card__sub">{{ cardSub }}</p>
        </div>
        <button v-if="pill" type="button" class="card__pill" :aria-label="pill.label" @click="filterOpen = true">{{ pill.text }}</button>
      </div>
      <IonButtons slot="end">
        <IonButton :aria-label="view === 'wall' ? 'Switch to list view' : 'Switch to the wall'" @click="toggleView">
          <IonIcon slot="icon-only" :icon="view === 'wall' ? listOutline : gridOutline" />
        </IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true" :scroll-y="view === 'list'" class="shelf-content">
      <!-- The wall ------------------------------------------------------------ -->
      <template v-if="view === 'wall'">
        <div ref="stage" slot="fixed" class="stage" :style="stageStyle">
          <!-- Not built behind first-run onboarding: it covers the whole screen,
               and racking 115 bags under it only delays the cards. -->
          <ShelfWall
            v-if="settings.onboarded"
            ref="wall"
            class="stage__wall"
            :bays="bays"
            :matches="activeFilterCount ? matchingSlugs : null"
            :picked="picked?.slug || null"
            :reduced-motion="reducedMotion"
            :active="wallActive"
            :initial-bay="String(route.query.bay || '')"
            :card-bottom="frame.cardBottom"
            :dock-top="frame.dockTop"
            @pick="onPick"
            @bay-in-view="(k) => { bayInView = k; }"
            @ready="onReady"
            @drag="hint?.dismiss()"
            @release="picked = null"
          />

          <Transition :name="reducedMotion ? 'fade-fast' : 'fade'">
            <div v-if="!ready" class="loading" role="status">
              <div class="loading__beans" aria-hidden="true">
                <img v-for="n in 4" :key="n" :src="BEANS[n - 1]" alt="" :style="{ '--i': n - 1 }">
              </div>
              <p>Stocking the shelves…</p>
            </div>
          </Transition>

          <div v-if="ready && noMatches && !picked" class="empty-card" role="status">
            <p class="empty-card__title">Nothing on the wall is <em>{{ filterSummary }}</em>.</p>
            <p class="empty-card__body">Every coffee is still here, just dimmed. Loosen a filter or clear them.</p>
            <button type="button" class="ink-btn" @click="clearFilters">Clear filters</button>
          </div>
        </div>

        <div ref="dock" slot="fixed" class="dock" :class="{ 'dock--away': !!picked }" :inert="!wallActive || undefined" :aria-hidden="!wallActive || undefined">
          <div class="dock__hint">
            <ShelfHint ref="hint" :ready="ready" />
          </div>
          <div class="dock__row">
            <GroupBar class="dock__group" />
            <button type="button" class="icon-btn" :class="{ 'icon-btn--on': activeFilterCount }" :aria-label="filterLabel" @click="filterOpen = true">
              <IonIcon :icon="optionsOutline" aria-hidden="true" />
              <span v-if="activeFilterCount" class="icon-btn__badge" aria-hidden="true">{{ activeFilterCount }}</span>
            </button>
            <button type="button" class="icon-btn" aria-label="See it in your room" :disabled="ar.busy.value" @click="ar.run()">
              <IonIcon :icon="cubeOutline" aria-hidden="true" />
            </button>
          </div>
          <BayStrip :bays="bays" :active="bayInView" :filtered="activeFilterCount > 0" @focus="focusBay" />
        </div>
      </template>

      <!-- The list ----------------------------------------------------------- -->
      <template v-else>
        <AppLargeTitle title="Shelf" />
        <div class="list-top">
          <div class="list-controls">
            <GroupBar class="list-controls__group" />
            <button type="button" class="icon-btn" :class="{ 'icon-btn--on': activeFilterCount }" :aria-label="filterLabel" @click="filterOpen = true">
              <IonIcon :icon="optionsOutline" aria-hidden="true" />
              <span v-if="activeFilterCount" class="icon-btn__badge" aria-hidden="true">{{ activeFilterCount }}</span>
            </button>
          </div>
        </div>
        <ShelfList v-if="!noMatches" :bays="bays" />
        <div v-else class="list-empty">
          <p class="empty-card__title">Nothing matches <em>{{ filterSummary }}</em>.</p>
          <button type="button" class="ink-btn" @click="clearFilters">Clear filters</button>
        </div>
      </template>
    </IonContent>

    <FilterSheet :open="filterOpen" @close="filterOpen = false" />
    <LabelSheet :coffee="picked" @close="closeLabel" @prev="step(-1)" @next="step(1)" @ar="arFromLabel" />
  </IonPage>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon, onIonViewDidEnter, onIonViewDidLeave, onIonViewWillLeave,
} from '@ionic/vue';
import {
  informationCircleOutline, listOutline, gridOutline, cubeOutline, optionsOutline,
} from 'ionicons/icons';
import { AppHeader, AppLargeTitle, useAction } from '../core/ui/index.js';
import { announce } from '../core/a11y.js';
import {
  bays, grouping, filters, clearFilters, activeFilterCount, matchingSlugs, coffees, coffeeBySlug,
  settings, reducedMotion, picked, flavourById, BREWS,
} from '../store.js';
import ShelfWall from '../components/ShelfWall.vue';
import GroupBar from '../components/GroupBar.vue';
import BayStrip from '../components/BayStrip.vue';
import FilterSheet from '../components/FilterSheet.vue';
import ShelfList from '../components/ShelfList.vue';
import ShelfHint from '../components/ShelfHint.vue';
import LabelSheet from '../components/LabelSheet.vue';
import { seeInRoom } from '../lib/see-in-room.js';

const router = useRouter();
const route = useRoute();

// The wall is DOM and photographs, so it needs no WebGL: only the person's choice decides.
const view = computed(() => (settings.viewMode === 'list' ? 'list' : 'wall'));
const wall = ref(null);
const hint = ref(null);
const ready = ref(false);
// The loader's beans (Gemini, cut from .appfactory/design/assets/bean-sheet.png by scripts/cut-beans.mjs).
const BEANS = [1, 2, 3, 4].map((n) => `${import.meta.env.BASE_URL || '/'}loading/bean-${n}.webp`.replace('//', '/'));
const bayInView = ref('');
const filterOpen = ref(false);
let active = true;
// False while another page (a pushed Coffee or Roaster page, another tab) is on
// screen: the wall stays in the DOM for the way back, but none of it is a control.
const wallActive = ref(true);

const goAbout = () => router.push({ name: 'about' });
function setView (v) { settings.viewMode = v; }
function toggleView () { setView(view.value === 'wall' ? 'list' : 'wall'); }

// The QA harness's `empty` fixture (the plan maps the Shelf's `filtered-empty`
// state to it) sets `appfactory:qa:seed` before any app code runs; only the
// harness ever writes that key. The catalogue is never empty, so the Shelf's
// empty state IS "filters that match nothing": force one, and the real card renders.
const qaSeed = (() => {
  try { return localStorage.getItem('appfactory:qa:seed'); } catch { return null; }
})();
if (qaSeed === 'empty' || qaSeed === 'filtered-empty') filters.maxPrice = 0;

const noMatches = computed(() => matchingSlugs.value.size === 0);
const filterSummary = computed(() => [
  ...filters.flavours.map((f) => flavourById[f]?.short || f),
  ...filters.brews, ...filters.kinds, ...filters.origins,
  filters.inStockOnly ? 'In stock' : null,
  filters.maxPrice != null ? `Under $${filters.maxPrice}` : null,
].filter(Boolean).join(' + '));
const filterLabel = computed(() => (activeFilterCount.value ? `Filter, ${activeFilterCount.value} on` : 'Filter'));

// --- the glass title card -----------------------------------------------------------

const current = computed(() => bays.value.find((b) => b.key === bayInView.value) || bays.value[0]);
const cardTitle = computed(() => {
  const t = current.value?.title || 'The wall';
  // "Brother Of Mine Coffee Roasters" → "Brother Of Mine": the card is one line.
  // With the pill beside it, "Vacation Coffee" → "Vacation".
  const room = pill.value ? 0 : 20;
  return t.length > room ? t.replace(/\s+(coffee\s+)?roasters$/i, '').replace(/\s+coffee$/i, '') : t;
});
const cardSub = computed(() => {
  const b = current.value; if (!b) return '';
  const i = bays.value.indexOf(b) + 1;
  const n = b.coffees.length;
  const count = activeFilterCount.value ? `${b.matchCount} of ${n} lit` : `${n} ${n === 1 ? 'coffee' : 'coffees'}`;
  return `${count} · Bay ${i} of ${bays.value.length}`;
});
// The mustard pill: the active filter, or what this bay brews.
const pill = computed(() => {
  if (activeFilterCount.value) {
    const s = filterSummary.value.split(' + ');
    const text = `Filter · ${s[0]}${s.length > 1 ? ` +${s.length - 1}` : ''}`;
    return { text, label: `Filters on: ${filterSummary.value}. Change filters` };
  }
  const b = current.value;
  if (!b || grouping.value === 'brew') return null;
  const mix = BREWS.filter((x) => b.coffees.some((c) => c.brew === x));
  if (!mix.length || mix.length === BREWS.length) return null;
  const text = mix.length === 1 ? `${mix[0]} roasts` : mix.join(' · ');
  return { text, label: `This bay: ${mix.join(' and ')} roasts. Filter the wall` };
});

// --- the wall ---------------------------------------------------------------------------

function onReady () {
  ready.value = true;
  if (import.meta.env.DEV) window.__wall = wall.value;
}

function onPick ({ slug }) {
  const c = coffeeBySlug(slug);
  if (c) { picked.value = c; hint.value?.dismiss(); }
}
function closeLabel () { picked.value = null; }

function focusBay (key) { wall.value?.focusBay(key); }

// --- framing: the shelves fill the band between the card and the dock ------------------
//
// The wall runs from the top of the screen (under the status bar and the glass
// card) to just past the dock's rounded top edge, and ShelfWall scales and
// offsets the photo so the top board's bags clear the card and the bottom
// board's lip sits just above the dock. Both edges are measured here, in the
// stage's coordinates. The card is measured as if its title ran to two lines
// (a long bay name wraps), so the wall never re-scales as you walk the bays.
const stage = ref(null);
const dock = ref(null);
const frame = ref({ cardBottom: null, dockTop: null });
function measureFrame () {
  const st = stage.value; const dk = dock.value;
  if (view.value !== 'wall' || !st || !dk) return;
  const top = st.getBoundingClientRect().top;
  // The dock slides 24px down while a bag is picked: measure where it rests.
  const shift = new window.DOMMatrixReadOnly(getComputedStyle(dk).transform === 'none' ? undefined : getComputedStyle(dk).transform).m42 || 0;
  const dockTop = Math.round(dk.getBoundingClientRect().top - shift - top);
  const bar = document.querySelector('.shelf-page--wall .shelf-header ion-toolbar');
  const title = bar?.querySelector('.card__title');
  let cardBottom = 0;
  if (bar) {
    cardBottom = bar.getBoundingClientRect().bottom - top;
    if (title) {
      const lh = parseFloat(getComputedStyle(title).lineHeight) || title.getBoundingClientRect().height;
      const lines = Math.max(1, Math.round(title.getBoundingClientRect().height / lh));
      cardBottom += Math.max(0, 2 - lines) * lh;
    }
  }
  cardBottom = Math.round(cardBottom);
  if (dockTop <= cardBottom) return;
  // A pixel of rounding as the title changes bay to bay must not re-scale the wall.
  const f = frame.value;
  const moved = (a, b) => a == null || Math.abs(a - b) >= 3;
  if (moved(f.cardBottom, cardBottom) || moved(f.dockTop, dockTop)) frame.value = { cardBottom, dockTop };
}
// Phone: the stage stops just past the dock's rounded corners (the photo shows
// through them), so nothing that scrolls sits under the dock's controls.
// Desktop (≥900px): the dock floats mid-screen, so the stage is full height.
const stageStyle = computed(() => (frame.value.dockTop ? { '--stage-h': `${frame.value.dockTop + 26}px` } : {}));
let frameRO = null;
function observeFrame () {
  frameRO?.disconnect();
  if (view.value !== 'wall' || typeof ResizeObserver === 'undefined') return;
  frameRO = new ResizeObserver(() => measureFrame());
  const bar = document.querySelector('.shelf-page--wall .shelf-header ion-toolbar');
  [stage.value?.parentElement, dock.value, bar].forEach((el) => el && frameRO.observe(el));
  measureFrame();
}
watch(view, async () => { await nextTick(); observeFrame(); });

watch(grouping, (g) => { announce(`Wall re-racked by ${g}`); });
// Deep link from a Roaster page: /tabs/shelf?bay=<slug>.
// (ShelfWall opens on `initialBay`; a later link while the wall is up pans there.)
watch(() => route.query.bay, (key) => { if (key && ready.value) wall.value?.focusBay(String(key)); });
// Re-entering the wall view: land on the bay you were on.
watch(view, async (v) => {
  if (v !== 'wall') { ready.value = false; return; }
  const key = bayInView.value;
  await nextTick();
  if (key) wall.value?.focusBay(key, { instant: true });
});

/** Prev/next: the neighbouring bag in the same bay, board by board, left to right. */
function step (dir) {
  const c = picked.value;
  if (!c) return;
  const it = wall.value?.itemOf(c.slug);
  const bay = bays.value.find((b) => b.key === it?.bay) || bays.value.find((b) => b.coffees.some((x) => x.slug === c.slug));
  if (!bay) return;
  const pos = (x) => { const p = wall.value?.itemOf(x.slug); return p ? [p.board, p.plate + p.x] : [0, 0]; };
  const order = bay.coffees
    .filter((x) => matchingSlugs.value.has(x.slug) || x.slug === c.slug)
    .map((x) => ({ x, p: pos(x) }))
    .sort((a, b) => (a.p[0] - b.p[0]) || (a.p[1] - b.p[1]))
    .map((o) => o.x);
  const i = order.findIndex((x) => x.slug === c.slug);
  const next = order[(i + dir + order.length) % order.length];
  if (next && next.slug !== c.slug) picked.value = next;
}

// --- AR --------------------------------------------------------------------------------

// The whole shop, walkable, wherever the device can (src/lib/see-in-room.js):
// the native iOS screen, WebXR AR in the browser, the bay in AR Quick Look on
// iPhone Safari, else the same shop as a 3D walk-around in the page.
const ar = useAction(async (key, pick, size) => {
  const bayKey = key || wall.value?.currentBayKey || bays.value[0]?.key;
  if (!bayKey) return;
  await seeInRoom({ bayKey, pick, size });
}, { failed: 'Couldn’t open AR just now. Try again.' });

// Back from the native AR walk: land on the bay the person walked to.
function onArClosed (e) {
  const key = e?.detail?.bayKey;
  if (key && bays.value.some((b) => b.key === key)) wall.value?.focusBay(key, { instant: true });
}

function arFromLabel (size) {
  const it = picked.value && wall.value?.itemOf(picked.value.slug);
  ar.run(it?.bay, picked.value?.slug, size);
}

// --- keyboard: ←/→ walk the bays from anywhere on the page, Esc puts the bag back -------------

function onWindowKey (e) {
  if (view.value !== 'wall' || !active || filterOpen.value) return;
  if (e.key === 'Escape' && picked.value) { picked.value = null; e.preventDefault(); return; }
  if (picked.value || e.target !== document.body) return;
  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    wall.value?.walk(e.key === 'ArrowRight' ? 1 : -1);
    hint.value?.dismiss();
    e.preventDefault();
  }
}

onMounted(() => {
  window.addEventListener('keydown', onWindowKey);
  window.addEventListener('resize', measureFrame);
  window.addEventListener('shelf-ar:closed', onArClosed);
  nextTick(observeFrame);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKey);
  window.removeEventListener('resize', measureFrame);
  window.removeEventListener('shelf-ar:closed', onArClosed);
  frameRO?.disconnect();
});
onIonViewDidEnter(() => { active = true; wallActive.value = true; measureFrame(); });
onIonViewWillLeave(() => { wallActive.value = false; });
onIonViewDidLeave(() => { active = false; });
void coffees;
</script>

<style scoped>
/* --- The glass card: AppHeader's toolbar, floating over the photograph. ---------- */
.shelf-page--wall .shelf-header {
  position: absolute;
  top: 0; left: 0; right: 0;
  background: transparent;
  box-shadow: none;
  padding: calc(var(--ion-safe-area-top, 0px) + 8px) 16px 0;
  pointer-events: none;
}
.shelf-page--wall .shelf-header::after { display: none; }
/* iOS draws a translucent header as a full-width blurred layer behind the whole
   header band (status bar + card). Over the photograph that reads as a smear
   above the shelves; only the card itself is glass here. */
.shelf-page--wall .shelf-header :deep(.header-background) { display: none; }
.shelf-page--wall .shelf-header :deep(ion-toolbar) {
  pointer-events: auto;
  --background: transparent;
  --border-width: 0;
  --min-height: 60px;
  --padding-start: 2px;
  --padding-end: 2px;
  --padding-top: 0;
  --padding-bottom: 0;
  padding-top: 0 !important;
  border-radius: var(--radius, 16px);
  background: var(--glass, rgba(250, 247, 241, 0.86));
  -webkit-backdrop-filter: blur(var(--glass-blur, 9px)) saturate(1.1);
  backdrop-filter: blur(var(--glass-blur, 9px)) saturate(1.1);
  box-shadow: 0 6px 20px -12px rgba(40, 30, 20, 0.45);
  --color: var(--ink, #1F2A22);
}
/* The bar keeps its name for assistive tech; the card shows the bay instead. */
.shelf-page--wall .shelf-header :deep(ion-title) {
  position: absolute !important;
  width: 1px; height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  padding: 0;
}
.shelf-page--wall .shelf-header :deep(ion-toolbar ion-button) {
  --color: var(--ink, #1F2A22);
  --min-height: 44px;
  --min-width: 44px;
}

.card { display: flex; align-items: center; gap: 10px; min-width: 0; padding: 8px 0; }
.card__text { flex: 1 1 auto; min-width: 0; }
.card__title {
  margin: 0;
  font-family: var(--font-display, 'Bricolage Grotesque', sans-serif);
  font-weight: 800;
  font-size: var(--text-xl);
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--ink, #1F2A22);
  /* A long bay name steps down a size before it would ever truncate. */
  font-size: clamp(var(--text-lg), 5.6vw, var(--text-xl));
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card__sub {
  margin: 3px 0 0;
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-weight: 600;
  font-size: var(--text-xs);
  color: var(--muted, #5E6B60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
/* The box is 44 tall; the mustard pill is its 26px padding box. The border is
   transparent and the background clipped to the padding, and the negative
   margin gives the 9px a side back, so the card lays out exactly as before. */
.card__pill {
  flex: none;
  max-width: calc(54% + 18px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 9px solid transparent;
  margin: -9px;
  background-clip: padding-box;
  border-radius: var(--radius-pill, 999px);
  padding: 6px 10px;
  background-color: var(--mustard, #F2C230);
  color: var(--ink, #1F2A22);
  font: 700 12px/1.2 var(--font-body, 'Manrope', sans-serif);
  cursor: pointer;
  position: relative;
}
.card__pill:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: -7px; }

.skip {
  position: absolute;
  z-index: 20;
  top: 8px;
  left: 8px;
  min-height: 44px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--ink, #1F2A22);
  color: var(--dock, #FAF7F1);
  font: 700 14px var(--font-body, 'Manrope', sans-serif);
  border: 0;
  white-space: nowrap;
  clip-path: inset(50%);
}
.skip:focus { clip-path: none; }

.shelf-page--wall .shelf-content { --background: var(--paper, #F4EFE6); }

/* --- Stage: from the top of the screen to just past the dock's rounded top edge. ---
   The photo runs under the status bar and the card; ShelfWall frames the shelves
   between them (see measureFrame). Nothing scrollable sits under the dock's controls. */
.stage { position: absolute; top: 0; left: 0; right: 0; height: var(--stage-h, 100%); overflow: hidden; }
.stage__wall { position: absolute; inset: 0; }
/* The photo runs under the status bar; a soft shade there keeps the clock and
   battery legible over the leaves without reading as a band. */
.stage::after {
  content: '';
  position: absolute;
  left: 0; right: 0; top: 0;
  height: calc(var(--ion-safe-area-top, 0px) + 44px);
  background: linear-gradient(rgba(24, 20, 14, 0.58), rgba(24, 20, 14, 0.18) 60%, rgba(24, 20, 14, 0));
  pointer-events: none;
  z-index: 2;
}

.loading {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 18px;
  background: var(--paper, #F4EFE6);
  color: var(--muted, #5E6B60);
  font: 700 13px var(--font-body, 'Manrope', sans-serif);
  z-index: 3;
}
.loading p { margin: 0; }
.loading__beans { position: relative; width: 150px; height: 96px; }
.loading__beans img {
  position: absolute;
  left: calc(50% - 15px);
  top: 0;
  width: 30px;
  height: 30px;
  object-fit: contain;
  filter: drop-shadow(-2px 3px 2px rgba(40, 26, 12, .35));
  opacity: 0;
  animation: bean-drop 2s cubic-bezier(.45, 0, .55, 1) infinite;
  animation-delay: calc(var(--i) * 500ms);
}
.loading__beans img:nth-child(1) { --x0: -38px; --x1: -30px; --spin: 300deg; }
.loading__beans img:nth-child(2) { --x0: 22px; --x1: 8px; --spin: -260deg; }
.loading__beans img:nth-child(3) { --x0: -6px; --x1: -10px; --spin: 340deg; }
.loading__beans img:nth-child(4) { --x0: 40px; --x1: 30px; --spin: -300deg; }
/* Each bean tumbles in, lands, bounces once, settles, fades: a slow pour. */
@keyframes bean-drop {
  0% { opacity: 0; transform: translate(var(--x0), -40px) rotate(0deg); }
  8% { opacity: 1; }
  42% { transform: translate(var(--x1), 60px) rotate(var(--spin)); animation-timing-function: cubic-bezier(.2, .7, .4, 1); }
  52% { transform: translate(calc(var(--x1) + 3px), 46px) rotate(calc(var(--spin) + 25deg)); animation-timing-function: cubic-bezier(.6, 0, .8, .4); }
  62%, 84% { opacity: 1; transform: translate(calc(var(--x1) + 5px), 60px) rotate(calc(var(--spin) + 40deg)); }
  100% { opacity: 0; transform: translate(calc(var(--x1) + 5px), 60px) rotate(calc(var(--spin) + 40deg)); }
}

.empty-card {
  position: absolute;
  left: 50%;
  top: 46%;
  transform: translate(-50%, -50%);
  width: min(calc(100% - 32px), 340px);
  padding: 22px 20px 20px;
  border-radius: var(--radius, 16px);
  background: var(--dock, #FAF7F1);
  color: var(--ink, #1F2A22);
  text-align: center;
  box-shadow: var(--shadow-card, 0 10px 30px -14px rgba(40, 30, 20, 0.32));
  z-index: 3;
}
.empty-card__title {
  margin: 0 0 6px;
  font: 800 20px/1.15 var(--font-display, 'Bricolage Grotesque', sans-serif);
  letter-spacing: -0.02em;
}
.empty-card__title em { font-style: normal; color: var(--leaf, #3F7A4E); }
.empty-card__body { margin: 0 0 16px; font: 500 14px/1.45 var(--font-body, 'Manrope', sans-serif); color: var(--ink-2, #3E4A41); }
.ink-btn {
  min-height: var(--btn-height, 52px);
  padding: 0 26px;
  border: 0;
  border-radius: var(--radius-pill, 999px);
  background: var(--ink, #1F2A22);
  color: var(--dock, #FAF7F1);
  font: 800 15px var(--font-body, 'Manrope', sans-serif);
  cursor: pointer;
}
.ink-btn:active { transform: scale(0.97); }
.ink-btn:focus-visible { outline: 2px solid var(--mustard, #F2C230); outline-offset: 2px; }

/* --- The dock -------------------------------------------------------------------------- */
.dock {
  --dock-h: 112px;
  position: absolute;
  left: 0; right: 0; bottom: 0;
  z-index: 5;
  padding: 14px 16px 2px;
  background: var(--dock, #FAF7F1);
  border-radius: var(--radius-sheet, 24px) var(--radius-sheet, 24px) 0 0;
  box-shadow: var(--shadow-dock, 0 -10px 24px rgba(40, 30, 20, 0.14));
  transition: transform 260ms cubic-bezier(.2, .8, .2, 1), opacity 200ms ease-out;
}
.dock--away { transform: translateY(24px); opacity: 0; pointer-events: none; }
.dock__hint { position: absolute; left: 0; right: 0; bottom: calc(100% + 12px); display: flex; justify-content: center; pointer-events: none; }
.dock__row { display: flex; align-items: center; gap: 6px; }
.dock__group { flex: 1 1 auto; min-width: 0; }

/* A 44px box; the 40px sage disc is its padding box (transparent border,
   background clipped to the padding), and the -2px margin keeps the row's
   layout exactly as it was with a 40px button. */
.icon-btn {
  position: relative;
  flex: none;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin: -2px;
  border: 2px solid transparent;
  background-clip: padding-box;
  border-radius: 50%;
  background-color: var(--sage, #E6EDE4);
  color: var(--ink-2, #3E4A41);
  font-size: var(--text-xl);
  cursor: pointer;
  transition: transform 140ms ease-out, background-color 180ms ease-out;
}
.icon-btn:active { transform: scale(0.94); }
.icon-btn:disabled { opacity: 0.5; }
.icon-btn--on { background-color: var(--ink, #1F2A22); color: var(--dock, #FAF7F1); }
.icon-btn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 0; }
.icon-btn__badge {
  position: absolute;
  top: -3px; right: -3px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: grid; place-items: center;
  border-radius: var(--radius-pill, 999px);
  background: var(--mustard, #F2C230);
  color: var(--ink, #1F2A22);
  font: 800 11px/1 var(--font-body, 'Manrope', sans-serif);
  font-variant-numeric: tabular-nums;
}

/* Desktop: the card and dock are compact, the wall runs full height behind them. */
@media (min-width: 900px) {
  .shelf-page--wall .shelf-header { right: auto; width: min(560px, calc(100% - 32px)); padding-left: 24px; padding-top: calc(var(--ion-safe-area-top, 0px) + 16px); }
  .stage { height: auto; bottom: 0; }
  .dock { left: 50%; right: auto; width: min(720px, calc(100% - 48px)); transform: translateX(-50%); }
  .dock--away { transform: translate(-50%, 24px); }
}

/* --- The list view -------------------------------------------------------------------- */
.list-top { padding: 8px 16px 8px; }
.list-controls { display: flex; align-items: center; gap: 6px; }
.list-controls__group { flex: 1 1 auto; min-width: 0; }
.list-empty { padding: 56px 16px; text-align: center; }

.fade-enter-active, .fade-leave-active { transition: opacity 420ms ease-out; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 150ms linear; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .loading__beans img { animation: none; opacity: 1; transform: translate(calc((var(--i) - 1.5) * 26px), 60px); }
  .dock, .icon-btn { transition: none; }
}
</style>
